<template>
	<div class="flex flex-col h-full">
		<div class="textarea-wrapper">
			<textarea
				:ref="refAttr"
				:value="value"
				:placeholder="textareaPlaceholder"
				:maxlength="maxCharLength"
				class="resize-none px-1 mb-4 font-roboto w-full full-length-textarea border rounded focus:outline-none focus:ring-0"
				@input="handleInput"
				@focus="isFocused = true"
				@blur="isFocused = false"
			></textarea>

			<div v-if="isFocused" class="char-counter">
				<span>{{ countToDisplay }}/{{ maxCharLength }}</span>
			</div>
		</div>
		<div v-if="internalErrorMessage" class="text-red-500 text-base mb-2">
			{{ internalErrorMessage }}
		</div>
	</div>
</template>

<script>
import {
	NOTES_MAX_LENGTH,
	COMPLETION_NOTE_MIN_LENGTH,
} from '@/config/constants'

export default {
	computed: {
		charCount() {
			let currentValue = this.value
			const template = this.template
			const currentSpacesAndNewLines =
				currentValue.match(/\s|\n/g)?.length || 0
			const templateSpacesAndNewLines =
				template.match(/\s|\n/g)?.length || 0

			const templateLines = template
				.split('\n')
				.map((line, index) => (index === 0 ? line : `\n${line}`))

			if (!this.template) {
				currentValue = currentValue.trimStart()
			}

			if (
				this.hasExactLineSequence(currentValue, templateLines) &&
				currentSpacesAndNewLines >= templateSpacesAndNewLines
			) {
				return currentValue.length - template.length
			} else {
				return currentValue.trimStart().length
			}
		},
		countToDisplay() {
			return this.value.length
		},
	},
	methods: {
		handleInput(event) {
			this.$emit('input', event.target.value)
			// Clear error message on input
			if (this.internalErrorMessage) {
				this.internalErrorMessage = ''
			}
		},
		validateText() {
			this.internalErrorMessage = ''

			if (this.isRequired) {
				// If required, validate that charCount is between minCharLength and maxCharLength
				if (
					this.charCount < this.minCharLength ||
					this.charCount > this.maxCharLength
				) {
					this.internalErrorMessage = `A completion note of ${this.minCharLength}-${this.maxCharLength} characters is required`
					return false
				}
			} else {
				if (this.charCount > this.maxCharLength) {
					this.internalErrorMessage = `Please enter no more than ${this.maxCharLength} characters.`
					return false
				}
			}

			return true
		},
		hasExactLineSequence(currentValue, templateLines) {
			let index = 0

			for (const line of templateLines) {
				const foundAt = currentValue.indexOf(line, index)

				if (foundAt === -1) return false

				// Move index forward to enforce order
				index = foundAt + line.length
			}

			return true
		},
	},
	name: 'CompletionNoteTextArea',
	props: {
		// eslint-disable-next-line
		isRequired: {
			type: Boolean,
			default: false,
		},
		template: {
			type: String,
			default: '',
		},
		maxCharLength: {
			type: Number,
			default: NOTES_MAX_LENGTH,
		},
		minCharLength: {
			type: Number,
			default: COMPLETION_NOTE_MIN_LENGTH,
		},
		value: { type: String, default: '' },
		textareaPlaceholder: {
			type: String,
			default: 'Enter new note here...',
		},
		refAttr: { type: String, default: null },
		errorMessage: { type: String, default: '' },
	},
	watch: {
		charCount(newCount) {
			this.$emit('char-count-change', newCount)
		},
	},
	mounted() {
		if (this.value === '' && this.template) {
			this.$emit('input', this.template)
		}
		// Emit initial charCount
		this.$emit('char-count-change', this.charCount)
	},
	data() {
		return {
			internalErrorMessage: '',
			isFocused: false,
		}
	},
}
</script>
